import React, {
  useEffect, useMemo, useState,
} from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Collapse,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-au';
import { toInteger } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import ContentCard from '../../components/ContentCard';
import DateNavigator from '../../components/DateNavigator';
import PageContent from '../../components/PageContent';
import SkeletonStack from '../../components/SkeletonStack';
import { useGetLessonsQuery } from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { UserGroup } from '../../types/common';
import { getLessonSubject } from '../../utils/common/classes';
import {
  DEFAULT_LOCATION,
  SUBJECTS,
} from '../../utils/common/constants';
import useUserGroups from '../../utils/react/hooks/useUserGroups';

import {
  AlertContainer,
  ContentContainer,
  DefaultContentContainer,
  DividerContainer,
  Header,
  LessonsContainer,
  PageContainer,
  RollLessonsToggle,
  RollLessonsToggleBar,
  TimeDividerLabel,
  ToggleHeading,
} from './index.styles';
import RollLessonCard from './RollLessonCard';
import RollLessonDetails from './RollLessonDetails';

const now = dayjs();

const DefaultContent = () => (
  <DefaultContentContainer>
    <ContentCard>
      <Typography variant="overline">No lessons for selected filter</Typography>
    </ContentCard>
  </DefaultContentContainer>
);

const StaffRollPage = () => {
  const theme = useTheme();
  const userGroups = useUserGroups();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isAdmin = userGroups.includes(UserGroup.Admins);
  const isTeacher = userGroups.includes(UserGroup.Teachers);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date');
  const selectedId = searchParams.get('id');

  const [date, setDate] = useState<Dayjs>(selectedDate ? dayjs(toInteger(selectedDate)) : now);

  const { data: lessons,
    isLoading: isLessonsLoading } = useGetLessonsQuery({
    queryStartDatetime: date.startOf('day').valueOf(),
    queryEndDatetime: date.endOf('day').valueOf(),
    location: DEFAULT_LOCATION,
  });

  const { data: unmarkedLessons,
    isLoading: isUnmarkedLessonsLoading } = useGetLessonsQuery({
    queryEndDatetime: now.startOf('day').valueOf(),
    rollMarked: false,
  }, { skip: !isAdmin });

  const [selectedLesson, setSelectedLesson] = useState<Lesson | undefined>();
  const [showUnmarkedLessons, setShowUnmarkedLessons] = useState(false);

  // State variables for the Toggle bar
  const [allSubjectsToggled, setAllSubjectsToggled] = useState<boolean>(true);
  const [allTimesToggled, setAllTimesToggled] = useState<boolean>(true);
  const [subjectToggleStatus, setSubjectToggleStatus] = useState<{ [key: string]: boolean }>(
    {
      ...Object.fromEntries(Object.keys(SUBJECTS).map((subject) => [subject, true])),
    },
  );
  const [timeToggleStatus, setTimeToggleStatus] = useState<{ [key: number]: boolean }>({});
  const [uniqueTimes, setUniqueTimes] = useState<number[]>([]);

  useEffect(() => {
    if (lessons?.data) {
      const newUniqueTimes = [...new Set(lessons.data.map((lesson) => lesson.startDatetime))];
      setUniqueTimes(newUniqueTimes);
      setTimeToggleStatus(
        Object.fromEntries(newUniqueTimes.map((time) => [time, true])),
      );
    }
  }, [lessons?.data]);

  useEffect(() => {
    if (selectedId && lessons) {
      const lesson = lessons.data.find((l) => l.id === selectedId);
      if (lesson) {
        setSelectedLesson(lesson);
      }
    }
  }, [selectedId, lessons]);

  const deselectLesson = () => {
    setSelectedLesson(undefined);
    searchParams.delete('id');
    setSearchParams(searchParams);
  };

  const handleSelect = (lesson: Lesson) => {
    if (selectedLesson?.id === lesson.id) {
      deselectLesson();
    } else {
      setSelectedLesson(lesson);
      searchParams.set('id', lesson.id);
      setSearchParams(searchParams);
    }
  };

  const handleDateChange = (newDate: Dayjs) => {
    setDate(newDate);
    searchParams.set('date', newDate.valueOf().toString());
    setSearchParams(searchParams);
  };

  const toggleSubjectState = (targetState: string) => {
    setAllSubjectsToggled(false);
    if (allSubjectsToggled) {
      // If the 'All' flag is turned on, turn it off and turn on the target state
      setSubjectToggleStatus({
        ...Object.fromEntries(
          Object.keys(SUBJECTS).map((state) => [state, false]),
        ),
        [targetState]: true,
      });
    } else {
      setSubjectToggleStatus({
        ...subjectToggleStatus,
        [targetState]: !subjectToggleStatus[targetState],
      });
    }
  };

  const toggleAllSubjects = () => {
    if (allSubjectsToggled) {
      setSubjectToggleStatus(
        Object.fromEntries(
          Object.keys(SUBJECTS).map((state) => [state, false]),
        ),
      );
      setAllSubjectsToggled(false);
    } else {
      setSubjectToggleStatus(
        Object.fromEntries(
          Object.keys(SUBJECTS).map((state) => [state, true]),
        ),
      );
      setAllSubjectsToggled(true);
    }
  };

  const toggleTimeState = (targetState: number) => {
    setAllTimesToggled(false);
    if (allTimesToggled) {
      // If the 'All' flag is turned on, turn it off and turn on the target state
      setTimeToggleStatus({
        ...Object.fromEntries(uniqueTimes.map((time) => [time, false])),
        [targetState]: true,
      });
    } else {
      setTimeToggleStatus({
        ...timeToggleStatus,
        [targetState]: !timeToggleStatus[targetState],
      });
    }
  };

  const toggleAllTimes = () => {
    if (allTimesToggled) {
      setTimeToggleStatus(
        Object.fromEntries(uniqueTimes.map((time) => [time, false])),
      );
      setAllTimesToggled(false);
    } else {
      setTimeToggleStatus(
        Object.fromEntries(uniqueTimes.map((time) => [time, true])),
      );
      setAllTimesToggled(true);
    }
  };

  const filteredLessons = useMemo(() => lessons?.data.filter((lesson) => {
    const subject = getLessonSubject(lesson);
    const subjectSelected = subjectToggleStatus[subject];
    const timeSelected = timeToggleStatus[lesson.startDatetime];
    return subjectSelected && timeSelected;
  }), [lessons?.data, subjectToggleStatus, timeToggleStatus]);

  if (isLessonsLoading || isUnmarkedLessonsLoading) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <PageContent
      sideBar={selectedLesson ? (
        <RollLessonDetails
          selectedLesson={selectedLesson}
          onClose={() => deselectLesson()}
          isAdmin={isAdmin}
          isTeacher={isTeacher}
        />
      ) : <div />}
      sideBarOpen={!!selectedLesson}
      sideBarWidth={600}
    >
      <PageContainer>
        <ContentContainer>
          {(unmarkedLessons?.data?.length !== undefined &&
          unmarkedLessons?.data?.length > 0 &&
          isAdmin) && (
            <Stack direction="column" spacing={2}>
              <AlertContainer>
                <Alert
                  severity="error"
                  onClick={() => setShowUnmarkedLessons((prev) => !prev)}
                  action={(showUnmarkedLessons ?
                    <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />)}
                >
                  Rolls from the past have not been submitted. Please check attendance.
                </Alert>
              </AlertContainer>
              <Collapse
                in={showUnmarkedLessons}
                mountOnEnter
                unmountOnExit
              >
                <Stack direction="column" spacing={2}>
                  {unmarkedLessons?.data?.map((lesson) => (
                    <RollLessonCard
                      key={lesson.id}
                      lesson={lesson}
                      onSelect={() => handleSelect(lesson)}
                      selected={selectedLesson?.id === lesson.id}
                      rollUnmarked
                    />
                  ))}
                  <Box paddingTop={2}>
                    <Divider
                      orientation="horizontal"
                      flexItem
                    />
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          )}
          <Header>
            <DateNavigator date={date} onDateChange={handleDateChange} />
          </Header>
          <RollLessonsToggleBar>
            {isMd && (
              <Box
                display="flex"
                justifyContent="flex-start"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                >
                  <ToggleHeading>Time</ToggleHeading>
                  <RollLessonsToggle
                    $color="primary"
                    $selected={allTimesToggled}
                    onClick={toggleAllTimes}
                  >
                    All
                  </RollLessonsToggle>
                  {uniqueTimes.map((time) => (
                    <RollLessonsToggle
                      key={time}
                      $color="primary"
                      $selected={!allTimesToggled && timeToggleStatus[time]}
                      onClick={() => toggleTimeState(time)}
                    >
                      {dayjs(time).format('h:mm a')}
                    </RollLessonsToggle>
                  ))}
                </Stack>
              </Box>
            )}
          </RollLessonsToggleBar>
          <RollLessonsToggleBar>
            {isMd && (
              <Box
                display="flex"
                justifyContent="flex-start"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                >
                  <ToggleHeading>Subjects</ToggleHeading>
                  <RollLessonsToggle
                    $color="primary"
                    $selected={allSubjectsToggled}
                    onClick={toggleAllSubjects}
                  >
                    All
                  </RollLessonsToggle>
                  {Object.keys(SUBJECTS).map((subject) => (
                    <RollLessonsToggle
                      key={subject}
                      $color="primary"
                      $selected={!allSubjectsToggled && subjectToggleStatus[subject]}
                      onClick={() => toggleSubjectState(subject)}
                    >
                      {SUBJECTS?.[subject].label }
                    </RollLessonsToggle>
                  ))}
                </Stack>
              </Box>
            )}
          </RollLessonsToggleBar>
          <LessonsContainer>
            {
              (filteredLessons?.length === 0) ?
                <DefaultContent /> :
                filteredLessons?.map((lesson, i) => {
                  const card = (
                    <RollLessonCard
                      key={lesson.id}
                      lesson={lesson}
                      onSelect={() => handleSelect(lesson)}
                      selected={selectedLesson?.id === lesson.id}
                    />
                  );

                  const prevLesson = filteredLessons?.[i - 1];
                  if (prevLesson && dayjs(prevLesson.startDatetime).add(30, 'minute').isSameOrBefore(lesson.startDatetime)) {
                    // If the previous lesson is more than or equal to 30 minutes before,
                    // add a divider

                    return (
                      <Stack key={lesson.id} direction="column" spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <DividerContainer>
                            <Divider />
                          </DividerContainer>
                          <TimeDividerLabel>
                            <Typography variant="h5" color={theme.palette.gray.main}>
                              {dayjs(lesson.startDatetime).format('h:mm a')}
                            </Typography>
                          </TimeDividerLabel>
                          <DividerContainer>
                            <Divider />
                          </DividerContainer>
                        </Stack>
                        {card}
                      </Stack>
                    );
                  }

                  return card;
                })
            }
          </LessonsContainer>
        </ContentContainer>
      </PageContainer>
    </PageContent>
  );
};

export default StaffRollPage;
